import React, { InputHTMLAttributes, ForwardedRef } from "react";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { Checkbox } from "@core/components/ui/checkbox";
import { FormIcon } from "@core/components/forms/FormIcon";
import { CheckboxProps } from "@radix-ui/react-checkbox";
import { ErrorMessage } from "@core/components/forms/ErrorMessage";
import { FieldErrors } from "react-hook-form";
import { cn } from "@core/lib/utils";

export type LabeledCheckboxProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string | React.ReactNode;
  icon?: IconDefinition;
  iconClassName?: string;
  innerContainerClassName?: string;
  errors?: FieldErrors<any>;
  name: string;
} & CheckboxProps;

/**
 * LabeledCheckbox are checkbox inputs with a `<label>` and optional support for an icon.
 * @example
 * ```tsx
 * <LabeledCheckbox name="agreeTerms" label="Agree to Terms" icon={faCheckSquare} />
 * ```
 * They also support children, which are either displayed below the input or can be absolute:
 * ```tsx
 * <LabeledCheckbox {...}>
 *   <div className="absolute p-2 right-0">
 *     <FontAwesomeIcon icon={faInfoCircle} />
 *   </div>
 * </LabeledCheckbox>
 * ```
 */
export const LabeledCheckbox = React.forwardRef<HTMLButtonElement, LabeledCheckboxProps>(
  ({ children, className, icon, iconClassName, innerContainerClassName, label, errors, name, ...props }, ref) => {
    return (
      <div className={className}>
        <div className={cn("flex items-start", innerContainerClassName)}>
          {icon && <FormIcon icon={icon} className={iconClassName} />}
          <Checkbox id={name} name={name} {...props} ref={ref} />
          {label && (
            <label className="ml-2 text-sm" htmlFor={name}>
              {label}
            </label>
          )}
          {children}
        </div>
        {errors && name && (
          <ErrorMessage errors={errors} name={name} />
        )}
      </div>
    );
  }
);

LabeledCheckbox.displayName = "LabeledCheckbox";